import { ChevronLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

// Import API function and types
import { getAllocationData } from "../lib/api";
import type { AllocationData } from "../types";
import { useWallet } from "../lib/useWallet";
import { useToast } from "../lib/useToast";

// Import all images
import iconMiningDashboard from "../assets/images/allocation/mining.png";
import iconWeeklyTasks from "../assets/images/allocation/tasks.png";
import iconAirdrop from "../assets/images/allocation/airdrop.png";
import iconRefund from "../assets/images/allocation/refund.png";

export function CheckAllocationPage() {
  const navigate = useNavigate();
  const { address } = useWallet();
  const { showError } = useToast();
  const [allocationData, setAllocationData] = useState<AllocationData | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchAllocationData = async () => {
      console.log('CheckAllocationPage address', address)
      
      if (!address) {
        showError("Please connect to binance wallet");
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const data = await getAllocationData(address);
        setAllocationData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchAllocationData();
  }, [address, showError]);


  // 6秒后自动跳转到phase2-detail页面
  useEffect(() => {
    if (error || !allocationData || allocationData.plan_type === "Inactive") {
      const timer = setTimeout(() => {
        navigate('/phase2-detail');
      }, 6000);

      return () => clearTimeout(timer);
    }
  }, [error, allocationData, navigate]);

  // Format numbers for display
  const formatNumber = (num: number) => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="w-full min-h-screen bg-[#F2FBFE] flex items-center justify-center">
        <div className="text-gray-600 font-sf-pro font-medium text-lg">
          Loading...
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !allocationData || allocationData.plan_type === "Inactive") {
    return (
      <div className="w-full min-h-screen bg-[#F2FBFE] flex items-center justify-center">
        <div className="text-red-600 font-sf-pro font-medium text-lg">
          {"Please subscribe to the plan first!"}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-[#F2FBFE]">
      <div className="flex flex-col min-h-screen">
        {/* Header */}
        <div className="h-[60px] flex items-center justify-between px-4 relative">
          <button
            onClick={() => navigate(-1)}
            className="w-[30px] h-[25.71px] flex items-center justify-center"
          >
            <ChevronLeft className="w-6 h-6 text-gray-900" strokeWidth={2.5} />
          </button>

          {/* <button className="flex items-center gap-1.5 px-3 py-2">
            <div className="w-[18px] h-[18px] flex items-center justify-center">
              <Share2 className="w-4 h-4 text-[#E15CE6]" strokeWidth={2} />
            </div>
            <span className="text-[#E15CE6] font-sf-pro font-medium text-base">
              Invite
            </span>
          </button> */}
        </div>

        {/* Content */}
        <div className="flex-1 px-[13px] pt-4 pb-[34px] overflow-y-auto">
          {/* Title */}
          <h1 className="text-black font-inter font-bold text-[26px] sm:text-[30px] leading-[32px] sm:leading-[40px] text-center mb-[15px]">
            My Mining Rewards
          </h1>

          <div className="flex flex-col gap-[15px]">
            {/* Mining Dashboard Card */}
            <div className="bg-white border-[0.5px] border-[#CACFDB] rounded-xl p-[15px]">
              <div className="flex items-center gap-2.5 mb-[5px]">
                <div className="w-[40px] h-[40px] rounded-lg flex items-center justify-center">
                  <img
                    src={iconMiningDashboard}
                    alt="Dashboard"
                    className="w-8 h-8"
                  />
                </div>
                <h2 className="text-gray-900 font-sf-pro font-medium text-base sm:text-lg leading-[36px] sm:leading-[40px] flex-1">
                  Mining Dashboard
                </h2>
              </div>

              <div className="w-full h-px bg-[#CACFDB] mb-[5px]" />

              <div className="space-y-0">
                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] w-[140px] sm:w-[165px] break-words">
                    Subscription Tier
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-sm sm:text-[15px] leading-[20px] sm:leading-[22px] text-right flex-1 break-words">
                    {allocationData.plan_type}
                  </span>
                </div>

                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    Total Earnings
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {formatNumber(allocationData.total_earnings)} PIN
                  </span>
                </div>

                <div className="flex justify-between items-center h-8 ml-4">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    Free Pool Earnings
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {formatNumber(allocationData.free_pool_earnings)} PIN
                  </span>
                </div>

                <div className="flex justify-between items-center h-8 ml-4">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    Paid Pool Earnings
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {formatNumber(allocationData.paid_pool_earnings)} PIN
                  </span>
                </div>

                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    GOD Model Score
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {allocationData.ai_score}
                  </span>
                </div>

                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    This Week's Emission
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {allocationData.current_week_emission > 0 ? `${formatNumber(allocationData.current_week_emission)} PIN` : '0 PIN'}
                  </span>
                </div>

                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    Est. This Week
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {formatNumber(allocationData.current_week_estimate_earning)} PIN
                  </span>
                </div>
              </div>

              {allocationData.plan_type === "Free" ? (
                <>
                  <button
                    onClick={() => navigate("/phase2-detail")}
                    className="w-full bg-primary-400 rounded-xl px-6 py-[14px] mt-[10px] shadow-[0px_2px_3px_0px_rgba(204,205,207,0.15)] mb-[5px]"
                  >
                    <span className="text-white font-sf-pro font-bold text-base">
                      Upgrade
                    </span>
                  </button>
                  <div className="w-full h-8 mb-[5px] mt-[15px]">
                    <p className="w-full h-8 font-sf-pro font-normal text-[#e15ce6] text-[15px] text-center tracking-[0] leading-[22px]">
                      <span className="font-medium text-[#e15ce6]">
                        Boosted Airdrop
                      </span>
                      <span className="font-medium text-[#e15ce6] text-lg">
                        &nbsp;
                      </span>
                      <span className="font-bold text-[#e15ce6] text-lg">
                        x 80
                      </span>
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="mt-[10px] mb-[5px] font-sf-pro font-medium text-[#959AA3] text-[13px] text-center leading-[13px]">
                    View more data in PIN AI app
                  </p>
                </>
              )}
            </div>

            {/* Weekly Tasks Card */}
            <div className="bg-white border-[0.5px] border-[#CACFDB] rounded-xl p-[15px]">
              <div className="flex items-center gap-2.5 mb-[5px]">
                <div className="w-[40px] h-[40px] rounded-lg flex items-center justify-center">
                  <img
                    src={iconWeeklyTasks}
                    alt="Weekly Tasks"
                    className="w-8 h-8"
                  />
                </div>
                <h2 className="text-gray-900 font-sf-pro font-medium text-lg leading-[40px] w-[137px]">
                  Weekly Tasks
                </h2>
                <div className="flex items-center gap-2 ml-auto">
                  <div className="bg-[rgba(250,250,250,0.45)] border-[0.5px] border-[#E15CE6] rounded-xl px-2.5 py-1.5">
                    <span className="text-[#E15CE6] font-sf-pro font-medium text-[13px] leading-[17px]">
                      {allocationData.unfinished_tasks > 0
                        ? "Unfinished"
                        : "Completed"}
                    </span>
                  </div>
                  <span className="text-[#E15CE6] font-sf-pro font-medium text-[15px] leading-[40px]">
                    + {allocationData.weekly_task_boost}
                  </span>
                </div>
              </div>

              <div className="w-full h-px bg-[#CACFDB] mb-[5px]" />

              <div className="space-y-0">
                {allocationData.weekly_tasks.map((task, index) => (
                  <div key={index} className="flex items-center gap-3 h-8">
                    <div className="w-[25px] h-[25px] flex items-center justify-center">
                      <div
                        className={`w-5 h-5 rounded-full flex items-center justify-center ${
                          task.completed ? "bg-[#323232]" : "bg-[#CACFDB]"
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M13.5 4.5L6 12L2.5 8.5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    </div>
                    <span
                      className={`font-sf-pro font-medium text-[15px] leading-[22px] w-[287px] ${
                        task.completed ? "text-gray-900" : "text-[#676C77]"
                      }`}
                    >
                      {task.title}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Airdrop Card */}
            <div className="bg-white border-[0.5px] border-[#CACFDB] rounded-xl p-[15px]">
              <div className="flex items-center gap-2.5 mb-[5px]">
                <div className="w-[40px] h-[40px] rounded-lg flex items-center justify-center">
                  <img src={iconAirdrop} alt="Airdrop" className="w-8 h-8" />
                </div>
                <h2 className="text-gray-900 font-sf-pro font-medium text-lg leading-[40px] w-[117px]">
                  Airdrop
                </h2>
              </div>

              <div className="w-full h-px bg-[#CACFDB] mb-[5px]" />

              <div className="space-y-0">
                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    Locked
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {formatNumber(allocationData.airdrop.locked_amount)} PIN
                  </span>
                </div>

                <div className="flex justify-between items-center h-8">
                  <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                    Claimable
                  </span>
                  <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                    {formatNumber(allocationData.airdrop.claimable_amount)} PIN
                  </span>
                </div>
              </div>

              <button className="w-full bg-primary-400 rounded-xl px-6 py-[14px] mt-[5px] shadow-[0px_2px_3px_0px_rgba(204,205,207,0.15)]">
                <span className="text-white font-sf-pro font-bold text-base">
                  Claim
                </span>
              </button>
            </div>

            {/* Refund Card - Only show for non-Free users */}
            {allocationData.plan_type !== "Free" && (
              <div className="bg-white border-[0.5px] border-[#CACFDB] rounded-xl p-[15px]">
                <div className="flex items-center gap-2.5 mb-[5px]">
                  <div className="w-[40px] h-[40px] rounded-lg flex items-center justify-center">
                    <img src={iconRefund} alt="Refund" className="w-8 h-8" />
                  </div>
                  <h2 className="text-gray-900 font-sf-pro font-medium text-lg leading-[40px] w-[117px]">
                    Refund
                  </h2>
                </div>

                <div className="w-full h-px bg-[#CACFDB] mb-[5px]" />

                <div className="space-y-0">
                  <div className="flex justify-between items-center h-8">
                    <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px] w-[165px]">
                      Refundable amount
                    </span>
                    <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                      {allocationData.refund.refundable_amount > 0
                        ? `${formatNumber(
                            allocationData.refund.refundable_amount
                          )} BNB`
                        : "0 BNB"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center h-8">
                    <span className="text-[#676C77] font-sf-pro font-medium text-[15px] leading-[22px]">
                      Time window
                    </span>
                    <span className="text-gray-900 font-sf-pro font-bold text-[15px] leading-[22px] w-[165px] text-right">
                      {allocationData.refund.time_window}
                    </span>
                  </div>
                </div>

                <button className="w-full bg-[rgba(117,207,221,0.3)] rounded-xl px-6 py-[14px] mt-[5px] shadow-[0px_2px_3px_0px_rgba(204,205,207,0.15)]">
                  <span className="text-white font-sf-pro font-bold text-base">
                    Apply
                  </span>
                </button>

                <p className="text-[#959AA3] font-sf-pro font-medium text-[11px] leading-[22px] text-center mt-0">
                  Refund Policy
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
